import { useCallback, useEffect, useState } from 'react';
import adminApiClient from '@/services/adminApiClient';
import type { UserDetail, UserListItem, UserStats } from '@/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLANS = ['free', 'student', 'pro', 'premium'];

const PLAN_COLORS: Record<string, string> = {
  free:    'bg-slate-100 text-slate-600',
  student: 'bg-blue-100 text-blue-700',
  pro:     'bg-violet-100 text-violet-700',
  premium: 'bg-amber-100 text-amber-700',
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLORS[plan] ?? 'bg-slate-100 text-slate-600'}`}>
      {plan}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
      active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
    }`}>
      {active ? 'Active' : 'Suspended'}
    </span>
  );
}

function StatCard({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="bg-white border border-admin-border rounded-xl p-5">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-3xl font-semibold text-slate-800 mt-1">{value.toLocaleString()}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// User detail panel
// ---------------------------------------------------------------------------

function UserDetailPanel({
  userId,
  onClose,
  onUserUpdated,
}: {
  userId: string;
  onClose: () => void;
  onUserUpdated: (id: string, patch: Partial<UserListItem>) => void;
}) {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    adminApiClient.get(`/users/${userId}`)
      .then(r => setUser(r.data))
      .catch(() => setError('Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  const toggleStatus = async () => {
    if (!user) return;
    setActionLoading('status');
    try {
      await adminApiClient.patch(`/users/${userId}/status`, { is_active: !user.is_active });
      setUser(u => u ? { ...u, is_active: !u.is_active } : u);
      onUserUpdated(userId, { is_active: !user.is_active });
    } catch {
      setError('Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm('Generate a temporary password for this user?')) return;
    setActionLoading('password');
    setTempPassword(null);
    try {
      const r = await adminApiClient.post(`/users/${userId}/reset-password`);
      setTempPassword(r.data.temp_password);
    } catch {
      setError('Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-admin-border">
          <h2 className="font-semibold text-slate-800">User Detail</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Loading…</div>
        ) : error && !user ? (
          <div className="flex-1 flex items-center justify-center text-red-500 text-sm">{error}</div>
        ) : user ? (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

            {/* Identity */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-admin-accent flex items-center justify-center text-white font-semibold text-lg shrink-0">
                {(user.name || user.email || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{user.name ?? '—'}</p>
                <p className="text-sm text-slate-500 truncate">{user.email}</p>
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  <PlanBadge plan={user.subscription.plan_id} />
                  <StatusBadge active={user.is_active} />
                  {user.role === 'admin' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">admin</span>
                  )}
                </div>
              </div>
            </div>

            {/* Profile */}
            <Section title="Profile">
              <Row label="Country" value={user.country} />
              <Row label="Skill Level" value={user.skill_level} />
              <Row label="Goal" value={user.goal} />
              <Row label="Exam Target" value={user.exam_target} />
              <Row label="Exam Timeline" value={user.exam_timeline} />
              <Row label="Grade / Year" value={user.grade_year} />
              <Row label="Daily Time" value={user.daily_time} />
              <Row label="Language" value={user.preferred_language} />
              <Row label="Onboarding" value={user.onboarding_completed ? 'Complete' : 'Incomplete'} />
              <Row label="Joined" value={fmt(user.created_at)} />
            </Section>

            {/* Subscription */}
            <Section title="Subscription">
              <Row label="Plan" value={<PlanBadge plan={user.subscription.plan_id} />} />
              <Row label="Status" value={user.subscription.status} />
              <Row label="Billing Cycle" value={user.subscription.billing_cycle} />
              <Row label="Period End" value={fmt(user.subscription.current_period_end)} />
              <Row label="Gateway" value={user.subscription.gateway} />
            </Section>

            {/* Usage */}
            <Section title="Usage This Month">
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Tutor Sessions" value={user.usage_this_month.tutor_sessions} />
                <MiniStat label="Exam Sessions" value={user.usage_this_month.exam_sessions} />
                <MiniStat label="Practice" value={user.usage_this_month.practice_sessions} />
                <MiniStat label="Smart Notes" value={user.usage_this_month.smart_notes} />
              </div>
            </Section>

            {/* Lifetime stats */}
            <Section title="Lifetime Stats">
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Sessions" value={user.lifetime_stats.total_sessions} />
                <MiniStat label="Exams" value={user.lifetime_stats.total_exams} />
                <MiniStat label="Practice" value={user.lifetime_stats.total_practice} />
                <MiniStat label="Topics Studied" value={user.lifetime_stats.topics_studied} />
              </div>
            </Section>

            {/* Recent sessions */}
            {user.recent_sessions.length > 0 && (
              <Section title="Recent Sessions">
                <div className="space-y-2">
                  {user.recent_sessions.map(s => (
                    <div key={s.id} className="flex justify-between items-center text-sm">
                      <span className="text-slate-700 truncate max-w-[200px]">{s.topic ?? 'Unknown topic'}</span>
                      <span className="text-slate-400 text-xs shrink-0 ml-2">{fmt(s.started_at)}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Temp password result */}
            {tempPassword && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 mb-1">Temporary Password</p>
                <p className="font-mono text-amber-900 text-sm break-all">{tempPassword}</p>
                <p className="text-xs text-amber-600 mt-1">Share this securely. It won't be shown again.</p>
              </div>
            )}

            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        ) : null}

        {/* Actions */}
        {user && (
          <div className="px-6 py-4 border-t border-admin-border flex gap-3">
            <button
              onClick={toggleStatus}
              disabled={actionLoading === 'status'}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${
                user.is_active
                  ? 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200'
                  : 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200'
              }`}
            >
              {actionLoading === 'status' ? 'Updating…' : user.is_active ? 'Suspend User' : 'Activate User'}
            </button>
            <button
              onClick={resetPassword}
              disabled={actionLoading === 'password'}
              className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors disabled:opacity-60"
            >
              {actionLoading === 'password' ? 'Resetting…' : 'Reset Password'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center text-sm py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right max-w-[240px] truncate">
        {value === null || value === undefined || value === '' ? '—' : value}
      </span>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-semibold text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Users() {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const PAGE_SIZE = 20;

  const fetchStats = useCallback(async () => {
    try {
      const r = await adminApiClient.get('/users/stats');
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchUsers = useCallback(async (currentPage: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        page_size: String(PAGE_SIZE),
        ...(search && { search }),
        ...(planFilter && { plan: planFilter }),
        ...(statusFilter && { is_active: statusFilter }),
      });
      const r = await adminApiClient.get(`/users?${params}`);
      setUsers(r.data.items);
      setTotal(r.data.total);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, planFilter, statusFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    const id = setTimeout(() => {
      setPage(1);
      fetchUsers(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search, planFilter, statusFilter, fetchUsers]);

  useEffect(() => { fetchUsers(page); }, [page, fetchUsers]);

  const handleUserUpdated = (id: string, patch: Partial<UserListItem>) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">Users</h1>
        <span className="text-sm text-slate-500">{total.toLocaleString()} total</span>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Total Users"       value={stats.total_users}          />
          <StatCard label="Active (30 days)"  value={stats.active_last_30_days}  sub="unique users with a session" />
          <StatCard label="Paying Users"      value={stats.paying_users}         sub="active paid subscriptions" />
          <StatCard label="New (7 days)"      value={stats.new_last_7_days}      />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-admin-border rounded-xl p-4 flex gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent"
        />
        <select
          value={planFilter}
          onChange={e => setPlanFilter(e.target.value)}
          className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent"
        >
          <option value="">All Plans</option>
          {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-admin-accent"
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Suspended</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-admin-border bg-slate-50">
              <th className="px-4 py-3 text-left font-medium text-slate-500">User</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Country</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Plan</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500">Sessions</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500">Exams</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500">Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading…</td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">No users found</td>
              </tr>
            )}
            {!loading && users.map(user => (
              <tr
                key={user.id}
                onClick={() => setSelectedUserId(user.id)}
                className="border-b border-admin-border hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-admin-accent/10 flex items-center justify-center text-admin-accent font-semibold text-sm shrink-0">
                      {(user.name || user.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate max-w-[180px]">{user.name ?? '—'}</p>
                      <p className="text-slate-400 text-xs truncate max-w-[180px]">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{user.country ?? '—'}</td>
                <td className="px-4 py-3"><PlanBadge plan={user.plan_id} /></td>
                <td className="px-4 py-3"><StatusBadge active={user.is_active} /></td>
                <td className="px-4 py-3 text-right text-slate-600">{user.session_count}</td>
                <td className="px-4 py-3 text-right text-slate-600">{user.exam_count}</td>
                <td className="px-4 py-3 text-slate-600">{fmt(user.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-admin-border">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-admin-border text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-slate-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg border border-admin-border text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedUserId && (
        <UserDetailPanel
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </div>
  );
}
